/**
 * ContextSelector — canonical shell Branch control.
 *
 * Organization identity is fixed by the Organization Runtime installation.
 * Client and portfolio are resource/report filters, not sticky global scope.
 */
import { useState, useEffect, useMemo, useRef } from 'react';
import {
  Box,
  Chip,
  Menu,
  MenuItem,
  CircularProgress,
  ListItemIcon,
  ListItemText,
} from '@mui/material';
import { useTheme } from '@/styles/use-finance-theme';
import { useTranslation } from 'react-i18next';
import AccountTreeOutlinedIcon from '@mui/icons-material/AccountTreeOutlined';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutlineOutlined';
import { useSession } from '../../features/auth/session-provider';
import { useNotify } from '../../features/notifications/notification-store';

export function ContextSelector() {
  const { t } = useTranslation();
  const theme = useTheme();
  const { notify } = useNotify();
  const {
    availableContexts,
    currentContext,
    switchContext,
    contextSwitchState,
    contextSwitchError,
  } = useSession();
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const triggerRef = useRef<HTMLElement | null>(null);
  const [announceMessage, setAnnounceMessage] = useState<string | null>(null);

  const currentOrgId = currentContext?.organizationId;
  const branchContexts = useMemo(() => {
    const byBranch = new Map<string, (typeof availableContexts)[number]>();
    for (const context of availableContexts) {
      if (!context.branchId) continue;
      if (currentOrgId && context.organizationId !== currentOrgId) continue;
      byBranch.set(context.branchId, context);
    }
    return [...byBranch.values()];
  }, [availableContexts, currentOrgId]);

  useEffect(() => {
    if (contextSwitchState === 'success') {
      setAnnounceMessage(t('shell:contextSelector.switchSuccess'));
    } else if (contextSwitchState === 'error') {
      const message = contextSwitchError ?? t('shell:contextSelector.switchError');
      setAnnounceMessage(message);
      notify(message, 'error', 7000);
    }
  }, [contextSwitchError, contextSwitchState, notify, t]);

  if (branchContexts.length <= 1 || !currentContext) return null;

  const handleSelect = async (membershipId: string, branchId: string) => {
    const focusTarget = anchorEl;
    setAnchorEl(null);
    window.requestAnimationFrame(() => (focusTarget as HTMLElement | null)?.focus());
    try {
      await switchContext(membershipId, branchId);
    } catch {
      // Session provider owns the state transition and safe user message.
    }
  };

  const isPending = contextSwitchState === 'pending';
  const isError = contextSwitchState === 'error';
  const displayLabel = currentContext.branchName ?? t('shell:contextSelector.fallback');

  return (
    <Box sx={{ display: 'flex', alignItems: 'center' }}>
      <Chip
        icon={
          isPending ? (
            <CircularProgress size={16} aria-hidden="true" />
          ) : isError ? (
            <ErrorOutlineIcon fontSize="small" color="error" />
          ) : (
            <AccountTreeOutlinedIcon fontSize="small" />
          )
        }
        label={displayLabel}
        onClick={event => {
            triggerRef.current = event.currentTarget as HTMLElement;
            setAnchorEl(event.currentTarget);
          }}
        variant="outlined"
        disabled={isPending}
        sx={{
          color: 'inherit',
          maxWidth: theme.financeLayout.shell.searchWidth,
          '& .MuiChip-label': {
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          },
        }}
        data-testid="branch-selector"
        aria-label={t('shell:contextSelector.ariaLabel')}
        aria-busy={isPending}
      />
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={() => {
            const focusTarget = anchorEl;
            setAnchorEl(null);
            window.requestAnimationFrame(() => (focusTarget as HTMLElement | null)?.focus());
          }}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
        slotProps={{
          paper: {
            sx: {
              width: 300,
              maxWidth: 'calc(100vw - 24px)',
              maxHeight: 'min(70vh, 560px)',
              overflowY: 'auto',
            },
          },
        }}
        data-testid="branch-selector-menu"
      >
        {branchContexts.map(context => {
          const isActive = currentContext.branchId === context.branchId;
          return (
            <MenuItem
              key={`${context.membershipId}-${context.branchId}`}
              selected={isActive}
              onClick={() => handleSelect(context.membershipId, context.branchId!)}
              data-testid="branch-selector-item"
              data-value={context.branchId}
            >
              <ListItemIcon>
                {isActive ? <CheckCircleIcon fontSize="small" /> : <Box sx={{ width: 20 }} />}
              </ListItemIcon>
              <ListItemText
                primary={context.branchName}
                secondary={context.branchCode}
                slotProps={{ primary: { noWrap: true }, secondary: { noWrap: true } }}
              />
            </MenuItem>
          );
        })}
      </Menu>
      <Box
        aria-live="polite"
        aria-atomic="true"
        sx={{
          position: 'absolute',
          width: '1px',
          height: '1px',
          overflow: 'hidden',
          clip: 'rect(0 0 0 0)',
        }}
      >
        {announceMessage}
      </Box>
    </Box>
  );
}
